import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import axios from 'axios';
import {Link, useNavigate} from 'react-router-dom';
import {
    ArrowClockwise,
    ArrowCounterclockwise,
    ArrowsFullscreen,
    BoxSeam,
    Broadcast,
    CheckCircleFill,
    ChevronDown,
    ChevronLeft,
    ChevronRight,
    ChevronUp,
    ClockHistory,
    GeoAltFill,
    GripHorizontal,
    GripVertical,
    Magic,
    Search,
    Signpost2,
    Sliders,
    Stopwatch,
    Truck,
    X,
} from 'react-bootstrap-icons';
import withAuth from '../../auth';
import {API_URL} from '../../constants';
import echo, {setEchoAuthToken} from '../../echo';
import {AuthContext} from '../AuthContext';
import {GoogleMapsProvider} from '../google_map/GoogleMapsContext';
import DispatchMap, {getDeliveryRouteColor} from './DispatchMap';
import TruckTypeIcon from './TruckTypeIcon';
import {
    DEFAULT_DRIVER_STATUS_FILTER,
    DRIVER_STATUS_FILTERS,
    formatRelativeTime,
    getDriverAssignedPickups,
    getDriverAvailability,
    getDriverConnectionLabel,
    getDriverLocationLabel,
    getDriverInitials,
    getDriverLocation,
    getDriverLocationSubscriptionIds,
    getDriverPickupSelection,
    getDriverTruckTypes,
    getNaviDispatchPickups,
    getPickupLocations,
    getSelectedAssignedPickups,
    hasPickupDriver,
    isDriverCompatible,
    isCompanyEmployeeDriver,
    isDispatchDriver,
    isDriverOnline,
    isLocationFresh,
    isNaviWhitelabelRecord,
    isScheduledPickup,
    mergeDriverLocations,
    mergeDriverPresence,
    matchesDriverStatusFilter,
    normalizeDriverLocationUpdate,
    normalizeDriverLocations,
    normalizeDriverPresence,
    normalizeDriverPresenceUpdate,
    sanitizeNaviWhitelabelDriver,
    sanitizeNaviWhitelabelPickup,
    togglePickupSelection,
    upsertPickup,
} from './dispatchUtils';
import {
    buildDriverRoutePlan,
    DEFAULT_STOP_SERVICE_MINUTES,
    getRoutePlanMetrics,
    moveRouteStop,
    optimizeRouteStopOrder,
} from './routePlanning';
import './DispatcherDashboard.css';
import {AXIOS_NOT_FOUND_REDIRECT_OPT_OUT} from '../../axiosNotFoundRedirect';

const authHeaders = (token) => ({Authorization: `Bearer ${token}`});
const PENDING_CHANNEL_MAX_AGE_MS = 30 * 60 * 1000;
const DISPATCH_LAYOUT_STORAGE_KEY = 'navi.dispatch.layout.v1';
const SIDEBAR_MIN_WIDTH = 280;
const SIDEBAR_MAX_WIDTH = 520;
const ROUTE_WORKSPACE_MIN_HEIGHT = 220;
const ROUTE_WORKSPACE_MAX_HEIGHT = 500;
const RESIZE_KEYBOARD_STEP = 16;
const MIN_MAP_WORKSPACE_WIDTH = 420;

const getLastPage = (response) => Number(
    response?.data?.last_page || response?.data?.meta?.last_page || 1
);

const getPageItems = (response) => Array.isArray(response?.data?.data) ? response.data.data : [];

const fetchAllPages = async (url, body, token, signal) => {
    const config = {headers: authHeaders(token), signal};
    const firstResponse = await axios.post(url, {...body, page: 1}, config);
    const lastPage = getLastPage(firstResponse);
    const firstPageItems = getPageItems(firstResponse);

    if (lastPage <= 1) return firstPageItems;

    const remainingResponses = [];
    const remainingPages = Array.from({length: lastPage - 1}, (_, index) => index + 2);

    for (let index = 0; index < remainingPages.length; index += 4) {
        const pageBatch = remainingPages.slice(index, index + 4);
        const batchResponses = await Promise.all(
            pageBatch.map((page) => axios.post(url, {...body, page}, config))
        );
        remainingResponses.push(...batchResponses);
    }

    return [firstPageItems, ...remainingResponses.map(getPageItems)].flat();
};

const fetchDriverLocations = async (token, signal) => {
    const response = await axios.get(`${API_URL}/api/admin/driver-locations`, {
        ...AXIOS_NOT_FOUND_REDIRECT_OPT_OUT,
        headers: authHeaders(token),
        params: {navi_only: true},
        signal,
    });

    return Array.isArray(response?.data?.data) ? response.data.data : [];
};

const recordIdKey = (recordId) => String(recordId);
const uniqueById = (items) => Array.from(
    new Map(items.filter((item) => item?.id !== undefined).map((item) => [recordIdKey(item.id), item])).values()
);

const isCompactViewport = () => typeof window !== 'undefined' && window.matchMedia('(max-width: 1199px)').matches;
const isMobileDispatchViewport = () => typeof window !== 'undefined' && window.matchMedia('(max-width: 767.98px)').matches;

const clampLayoutSize = (value, minimum, maximum) => Math.min(
    Math.max(Number(value) || minimum, minimum),
    maximum
);

const getInitialDispatchLayout = () => {
    const defaults = {
        driversWidth: isCompactViewport() ? 300 : 350,
        deliveriesWidth: isCompactViewport() ? 320 : 370,
        routeHeight: 300,
    };

    if (typeof window === 'undefined') return defaults;

    try {
        const stored = JSON.parse(window.localStorage.getItem(DISPATCH_LAYOUT_STORAGE_KEY) || '{}');
        return {
            driversWidth: clampLayoutSize(
                stored.driversWidth ?? defaults.driversWidth,
                SIDEBAR_MIN_WIDTH,
                SIDEBAR_MAX_WIDTH
            ),
            deliveriesWidth: clampLayoutSize(
                stored.deliveriesWidth ?? defaults.deliveriesWidth,
                SIDEBAR_MIN_WIDTH,
                SIDEBAR_MAX_WIDTH
            ),
            routeHeight: clampLayoutSize(
                stored.routeHeight ?? defaults.routeHeight,
                ROUTE_WORKSPACE_MIN_HEIGHT,
                ROUTE_WORKSPACE_MAX_HEIGHT
            ),
        };
    } catch (error) {
        return defaults;
    }
};

const getPickupStatusLabel = (pickup) => {
    if (!hasPickupDriver(pickup)) return 'Unassigned';
    return pickup.delivery_status?.status || pickup.delivery_status?.name || 'Assigned';
};

const getPickupStatusClass = (pickup) => {
    if (!hasPickupDriver(pickup)) return 'unassigned';

    const statusId = Number(pickup.delivery_status?.id ?? pickup.delivery_status_id);
    if (statusId >= 1 && statusId <= 4) return 'active';
    return 'assigned';
};

const formatScheduledTime = (pickup) => {
    const value = pickup?.scheduled_pickup_time || pickup?.waiting_for_driver_at;
    if (!value) return 'Now';

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'Now';

    return new Intl.DateTimeFormat('en-CA', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    }).format(date);
};

const formatRoutePlanTime = (value, includeDay = false) => new Intl.DateTimeFormat('en-CA', {
    ...(includeDay ? {weekday: 'short'} : {}),
    hour: 'numeric',
    minute: '2-digit',
}).format(new Date(value));

const formatRoutePlanWindow = (stop) => (
    `${formatRoutePlanTime(stop.startsAt, true)} – ${formatRoutePlanTime(stop.endsAt)}`
);

const formatRouteDuration = (minutes) => {
    const roundedMinutes = Math.max(0, Math.round(Number(minutes) || 0));
    const hours = Math.floor(roundedMinutes / 60);
    const remainingMinutes = roundedMinutes % 60;

    if (!hours) return `${remainingMinutes}m`;
    return remainingMinutes ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
};

const SidebarEmptyState = ({icon, title, copy}) => (
    <div className="dispatch-empty-state">
        <span>{icon}</span>
        <strong>{title}</strong>
        <p>{copy}</p>
    </div>
);

const DispatcherDashboard = () => {
    const {token, admin, setToken, hasPermission} = useContext(AuthContext);
    const navigate = useNavigate();
    const [drivers, setDrivers] = useState([]);
    const [pickups, setPickups] = useState([]);
    const [pendingPickupChannels, setPendingPickupChannels] = useState([]);
    const [driverLocations, setDriverLocations] = useState({});
    const [driverPresence, setDriverPresence] = useState({});
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [loadError, setLoadError] = useState('');
    const [locationError, setLocationError] = useState('');
    const [lastSync, setLastSync] = useState(null);
    const [socketConnected, setSocketConnected] = useState(Boolean(echo?.connector?.socket?.connected));
    const [now, setNow] = useState(Date.now());

    const [driversOpen, setDriversOpen] = useState(true);
    const [deliveriesOpen, setDeliveriesOpen] = useState(() => !isCompactViewport());
    const [dispatchLayout, setDispatchLayout] = useState(getInitialDispatchLayout);
    const [activeResize, setActiveResize] = useState(null);
    const [driverSearch, setDriverSearch] = useState('');
    const [driverStatusFilter, setDriverStatusFilter] = useState(DEFAULT_DRIVER_STATUS_FILTER);
    const [truckTypeFilter, setTruckTypeFilter] = useState('');
    const [pickupSearch, setPickupSearch] = useState('');
    const [pickupStatusFilter, setPickupStatusFilter] = useState('all');
    const [scheduledOnly, setScheduledOnly] = useState(true);
    const [selectedDriverId, setSelectedDriverId] = useState(null);
    const [selectedPickupId, setSelectedPickupId] = useState(null);
    const [manuallySelectedPickupIds, setManuallySelectedPickupIds] = useState([]);
    const [pickupSelectionSource, setPickupSelectionSource] = useState('pickup');
    const [fitRequest, setFitRequest] = useState(0);
    const [routeFitRequest, setRouteFitRequest] = useState(0);
    const [driverFocusRequest, setDriverFocusRequest] = useState(0);
    const [routeServiceMinutes, setRouteServiceMinutes] = useState(DEFAULT_STOP_SERVICE_MINUTES);
    const [routePlanStartAt, setRoutePlanStartAt] = useState(() => Date.now());
    const [routeStopOrder, setRouteStopOrder] = useState([]);
    const [routePlanMode, setRoutePlanMode] = useState('scheduled');
    const [routePlanNotice, setRoutePlanNotice] = useState('');
    const [draggedRouteStopKey, setDraggedRouteStopKey] = useState(null);
    const [routeTravelMetrics, setRouteTravelMetrics] = useState(null);
    const [routeWorkspaceCollapsed, setRouteWorkspaceCollapsed] = useState(() => isMobileDispatchViewport());
    const [assigning, setAssigning] = useState(false);
    const [assignmentMessage, setAssignmentMessage] = useState(null);
    const pickupEventVersionRef = useRef(new Map());
    const pickupEventSequenceRef = useRef(0);
    const latestLoadIdRef = useRef(0);
    const pickupSnapshotRequestSequenceRef = useRef(0);
    const latestAppliedPickupSnapshotRef = useRef(0);
    const driverSnapshotRequestSequenceRef = useRef(0);
    const latestAppliedDriverSnapshotRef = useRef(0);
    const pendingPickupAddedAtRef = useRef(new Map());
    const [dispatchPageElement, setDispatchPageElement] = useState(null);
    const [dispatchViewport, setDispatchViewport] = useState({width: 0, height: 0});
    const [routeWorkspaceElement, setRouteWorkspaceElement] = useState(null);
    const [routeWorkspaceBottom, setRouteWorkspaceBottom] = useState(0);
    const driversSidebarRef = useRef(null);
    const deliveriesSidebarRef = useRef(null);
    const [mapPadding, setMapPadding] = useState({top: 54, right: 398, bottom: 70, left: 378});

    const canAssign = hasPermission('pickup-update');
    const dispatchPickups = useMemo(
        () => getNaviDispatchPickups(pickups),
        [pickups]
    );

    const handleUnauthorized = useCallback(() => {
        localStorage.removeItem('authToken');
        setToken(null);
        navigate('/');
    }, [navigate, setToken]);

    const applyPickupSnapshot = useCallback((pickupItems, loadStartedAtSequence) => {
        const pickupSnapshot = uniqueById(
            pickupItems.map(sanitizeNaviWhitelabelPickup).filter(Boolean)
        );
        const liveChannelNames = new Set(pickupSnapshot.map((pickup) => pickup.channel_id).filter(Boolean));

        setPendingPickupChannels((current) => current.filter((channelName) => {
            if (!liveChannelNames.has(channelName)) return true;
            pendingPickupAddedAtRef.current.delete(channelName);
            return false;
        }));

        setPickups((current) => {
            const currentById = new Map(current.map((pickup) => [recordIdKey(pickup.id), pickup]));
            const reconciled = pickupSnapshot.flatMap((pickup) => {
                const id = recordIdKey(pickup.id);
                const eventArrivedDuringLoad = (pickupEventVersionRef.current.get(id) || 0) > loadStartedAtSequence;
                if (!eventArrivedDuringLoad) return [pickup];
                return currentById.has(id) ? [currentById.get(id)] : [];
            });
            const snapshotIds = new Set(pickupSnapshot.map((pickup) => recordIdKey(pickup.id)));
            current.forEach((pickup) => {
                const id = recordIdKey(pickup.id);
                const eventArrivedDuringLoad = (pickupEventVersionRef.current.get(id) || 0) > loadStartedAtSequence;
                if (!snapshotIds.has(id) && eventArrivedDuringLoad) reconciled.push(pickup);
            });
            return uniqueById(reconciled);
        });
    }, []);

    const loadData = useCallback(async (silent = false, signal) => {
        if (!token) return;

        const loadId = ++latestLoadIdRef.current;
        const pickupSnapshotId = ++pickupSnapshotRequestSequenceRef.current;
        const driverSnapshotId = ++driverSnapshotRequestSequenceRef.current;
        const loadStartedAtSequence = pickupEventSequenceRef.current;

        if (silent) setRefreshing(true);
        else setLoading(true);
        setLoadError('');

        try {
            const locationRequest = fetchDriverLocations(token, signal)
                .then((items) => ({items, error: null}))
                .catch((error) => ({items: [], error}));
            const [driverItems, pickupItems, locationResult] = await Promise.all([
                fetchAllPages(
                    `${API_URL}/api/admin/drivers`,
                    {sort_by: 'name', sort_order: 'asc', navi_only: true},
                    token,
                    signal
                ),
                fetchAllPages(
                    `${API_URL}/api/admin/pickups/live-requests`,
                    {navi_only: true},
                    token,
                    signal
                ),
                locationRequest,
            ]);

            if (signal?.aborted || loadId !== latestLoadIdRef.current) return;
            if (locationResult.error?.response?.status === 401) {
                handleUnauthorized();
                return;
            }

            const nextDrivers = uniqueById(
                driverItems.map(sanitizeNaviWhitelabelDriver).filter(Boolean)
            );
            if (driverSnapshotId > latestAppliedDriverSnapshotRef.current) {
                latestAppliedDriverSnapshotRef.current = driverSnapshotId;
                setDrivers(nextDrivers);
            }
            setLocationError(locationResult.error
                ? 'Stored driver locations are temporarily unavailable. Deliveries and assignments are still available.'
                : '');
            if (pickupSnapshotId > latestAppliedPickupSnapshotRef.current) {
                latestAppliedPickupSnapshotRef.current = pickupSnapshotId;
                applyPickupSnapshot(pickupItems, loadStartedAtSequence);
            }
            setDriverPresence((current) => mergeDriverPresence(
                mergeDriverPresence(current, normalizeDriverPresence(nextDrivers)),
                normalizeDriverPresence(locationResult.items)
            ));
            setDriverLocations((current) => {
                const seeded = {};
                nextDrivers.forEach((driver) => {
                    const location = getDriverLocation(driver);
                    if (location) seeded[driver.id] = location;
                });
                const stored = normalizeDriverLocations(locationResult.items);
                return mergeDriverLocations(current, {...seeded, ...stored});
            });
            setLastSync(Date.now());
            setFitRequest((request) => request + 1);
        } catch (error) {
            if (error?.code === 'ERR_CANCELED' || signal?.aborted) return;
            if (loadId !== latestLoadIdRef.current) return;
            if (error?.response?.status === 401) {
                handleUnauthorized();
                return;
            }

            setLoadError(error?.response?.data?.message || 'Unable to load dispatch data. Please try again.');
        } finally {
            if (!signal?.aborted && loadId === latestLoadIdRef.current) {
                setLoading(false);
                setRefreshing(false);
            }
        }
    }, [applyPickupSnapshot, handleUnauthorized, token]);

    const reconcileDriverLocations = useCallback(async (signal) => {
        if (!token) return;

        try {
            const locationItems = await fetchDriverLocations(token, signal);
            if (!signal?.aborted) {
                const stored = normalizeDriverLocations(locationItems);
                setDriverLocations((current) => mergeDriverLocations(current, stored));
                setDriverPresence((current) => mergeDriverPresence(current, normalizeDriverPresence(locationItems)));
                setLocationError('');
            }
        } catch (error) {
            if (error?.code === 'ERR_CANCELED' || signal?.aborted) return;
            if (error?.response?.status === 401) {
                handleUnauthorized();
                return;
            }
            setLocationError('Stored driver locations are temporarily unavailable. Deliveries and assignments are still available.');
        }
    }, [handleUnauthorized, token]);

    const reconcileDrivers = useCallback(async (signal) => {
        if (!token) return;

        const driverSnapshotId = ++driverSnapshotRequestSequenceRef.current;
        try {
            const driverItems = await fetchAllPages(
                `${API_URL}/api/admin/drivers`,
                {sort_by: 'name', sort_order: 'asc', navi_only: true},
                token,
                signal
            );
            if (!signal?.aborted && driverSnapshotId > latestAppliedDriverSnapshotRef.current) {
                latestAppliedDriverSnapshotRef.current = driverSnapshotId;
                const nextDrivers = uniqueById(driverItems.map(sanitizeNaviWhitelabelDriver).filter(Boolean));
                setDrivers(nextDrivers);
                setDriverPresence((current) => mergeDriverPresence(current, normalizeDriverPresence(nextDrivers)));
            }
        } catch (error) {
            if (error?.code === 'ERR_CANCELED' || signal?.aborted) return;
            if (error?.response?.status === 401) handleUnauthorized();
        }
    }, [handleUnauthorized, token]);

    const reconcilePickups = useCallback(async (signal) => {
        if (!token) return;

        const pickupSnapshotId = ++pickupSnapshotRequestSequenceRef.current;
        const loadStartedAtSequence = pickupEventSequenceRef.current;

        try {
            const pickupItems = await fetchAllPages(
                `${API_URL}/api/admin/pickups/live-requests`,
                {navi_only: true},
                token,
                signal
            );
            if (!signal?.aborted && pickupSnapshotId > latestAppliedPickupSnapshotRef.current) {
                latestAppliedPickupSnapshotRef.current = pickupSnapshotId;
                applyPickupSnapshot(pickupItems, loadStartedAtSequence);
            }
        } catch (error) {
            if (error?.code === 'ERR_CANCELED' || signal?.aborted) return;
            if (error?.response?.status === 401) handleUnauthorized();
        }
    }, [applyPickupSnapshot, handleUnauthorized, token]);

    useEffect(() => {
        const controller = new AbortController();
        loadData(false, controller.signal);
        return () => controller.abort();
    }, [loadData]);

    useEffect(() => {
        const timer = window.setInterval(() => setNow(Date.now()), 30000);
        return () => window.clearInterval(timer);
    }, []);

    useEffect(() => {
        const updateMapPadding = () => {
            const mobile = isMobileDispatchViewport();
            const pageWidth = dispatchPageElement?.clientWidth || 0;
            const viewportHeight = window.innerHeight || 700;
            const pageTop = dispatchPageElement?.getBoundingClientRect().top || 0;
            const pageHeight = Math.max(0, Math.floor(viewportHeight - pageTop));
            const driversWidth = driversSidebarRef.current?.offsetWidth || dispatchLayout.driversWidth;
            const deliveriesWidth = deliveriesSidebarRef.current?.offsetWidth || dispatchLayout.deliveriesWidth;
            const activeSheet = driversOpen
                ? driversSidebarRef.current
                : deliveriesOpen ? deliveriesSidebarRef.current : null;
            const sheetHeight = activeSheet?.offsetHeight || 390;
            const baseBottom = mobile && activeSheet
                ? Math.min(sheetHeight + 24, Math.max(90, pageHeight - 120))
                : mobile ? 24 : 28;
            const nextRouteWorkspaceBottom = mobile && activeSheet
                ? Math.min(sheetHeight, Math.max(38, pageHeight - 112))
                : 0;
            const routeWorkspaceHeight = routeWorkspaceElement?.offsetHeight || dispatchLayout.routeHeight;
            const routeAwareBottom = routeWorkspaceElement
                ? Math.min(
                    nextRouteWorkspaceBottom + routeWorkspaceHeight + 18,
                    Math.max(90, pageHeight - 72)
                )
                : baseBottom;
            const nextPadding = {
                top: 54,
                right: mobile ? 44 : deliveriesOpen ? deliveriesWidth + 28 : 70,
                bottom: routeAwareBottom,
                left: mobile ? 44 : driversOpen ? driversWidth + 28 : 70,
            };

            setDispatchViewport((current) => (
                current.width === pageWidth && current.height === pageHeight
                    ? current
                    : {width: pageWidth, height: pageHeight}
            ));

            setRouteWorkspaceBottom((current) => (
                current === nextRouteWorkspaceBottom ? current : nextRouteWorkspaceBottom
            ));

            setMapPadding((current) => (
                current.top === nextPadding.top
                && current.right === nextPadding.right
                && current.bottom === nextPadding.bottom
                && current.left === nextPadding.left
                    ? current
                    : nextPadding
            ));
        };

        updateMapPadding();
        window.addEventListener('resize', updateMapPadding);
        const resizeObserver = typeof window.ResizeObserver === 'function'
            ? new window.ResizeObserver(updateMapPadding)
            : null;
        const navbarElement = typeof document === 'undefined'
            ? null
            : document.querySelector('.admin-navbar');
        [navbarElement, dispatchPageElement, driversSidebarRef.current, deliveriesSidebarRef.current, routeWorkspaceElement]
            .filter(Boolean)
            .forEach((element) => resizeObserver?.observe(element));

        return () => {
            window.removeEventListener('resize', updateMapPadding);
            resizeObserver?.disconnect();
        };
    }, [
        deliveriesOpen,
        dispatchLayout.deliveriesWidth,
        dispatchLayout.driversWidth,
        dispatchLayout.routeHeight,
        dispatchPageElement,
        driversOpen,
        routeWorkspaceCollapsed,
        routeWorkspaceElement,
    ]);

    useEffect(() => {
        try {
            window.localStorage.setItem(DISPATCH_LAYOUT_STORAGE_KEY, JSON.stringify(dispatchLayout));
        } catch (error) {
            // Dispatch remains usable when browser storage is unavailable.
        }
    }, [dispatchLayout]);

    useEffect(() => {
        if (!activeResize) return undefined;

        const handleResizeMove = (event) => {
            event.preventDefault();
            const pointer = event.touches?.[0] || event;
            const delta = activeResize.kind === 'drivers'
                ? pointer.clientX - activeResize.startX
                : activeResize.kind === 'deliveries'
                    ? activeResize.startX - pointer.clientX
                    : activeResize.startY - pointer.clientY;
            const nextSize = clampLayoutSize(
                activeResize.startSize + delta,
                activeResize.minimum,
                activeResize.maximum
            );

            setDispatchLayout((current) => {
                if (current[activeResize.sizeKey] === nextSize) return current;
                return {...current, [activeResize.sizeKey]: nextSize};
            });
        };
        const finishResize = () => setActiveResize(null);
        const moveEventName = activeResize.inputType === 'touch' ? 'touchmove' : 'mousemove';
        const endEventNames = activeResize.inputType === 'touch'
            ? ['touchend', 'touchcancel']
            : ['mouseup'];

        window.addEventListener(moveEventName, handleResizeMove, {capture: true, passive: false});
        endEventNames.forEach((eventName) => window.addEventListener(eventName, finishResize, true));

        return () => {
            window.removeEventListener(moveEventName, handleResizeMove, true);
            endEventNames.forEach((eventName) => window.removeEventListener(eventName, finishResize, true));
        };
    }, [activeResize]);

    useEffect(() => {
        let controller = null;
        const reconcileTimer = window.setInterval(() => {
            controller?.abort();
            controller = new AbortController();
            reconcilePickups(controller.signal);
            reconcileDrivers(controller.signal);

            const cutoff = Date.now() - PENDING_CHANNEL_MAX_AGE_MS;
            setPendingPickupChannels((current) => current.filter((channelName) => {
                const addedAt = pendingPickupAddedAtRef.current.get(channelName) || Date.now();
                if (addedAt >= cutoff) return true;
                pendingPickupAddedAtRef.current.delete(channelName);
                return false;
            }));
        }, 60000);

        return () => {
            window.clearInterval(reconcileTimer);
            controller?.abort();
        };
    }, [reconcileDrivers, reconcilePickups]);

    useEffect(() => {
        let controller = null;
        const locationTimer = window.setInterval(() => {
            controller?.abort();
            controller = new AbortController();
            reconcileDriverLocations(controller.signal);
        }, 10000);

        return () => {
            window.clearInterval(locationTimer);
            controller?.abort();
        };
    }, [reconcileDriverLocations]);

    useEffect(() => {
        const mediaQuery = window.matchMedia('(max-width: 1199px)');
        const useSingleRailForCompactLayout = (event) => {
            if (event.matches) {
                setDriversOpen(true);
                setDeliveriesOpen(false);
            }
        };

        mediaQuery.addEventListener?.('change', useSingleRailForCompactLayout);
        return () => mediaQuery.removeEventListener?.('change', useSingleRailForCompactLayout);
    }, []);

    useEffect(() => {
        if (selectedDriverId === null) return;
        setRouteWorkspaceCollapsed(isMobileDispatchViewport());
    }, [selectedDriverId]);

    useEffect(() => {
        const mediaQuery = window.matchMedia('(max-width: 767.98px)');
        const adaptRouteWorkspaceToViewport = (event) => setRouteWorkspaceCollapsed(event.matches);

        mediaQuery.addEventListener?.('change', adaptRouteWorkspaceToViewport);
        return () => mediaQuery.removeEventListener?.('change', adaptRouteWorkspaceToViewport);
    }, []);

    useEffect(() => {
        const socket = echo?.connector?.socket;
        if (!socket) return undefined;

        const controller = new AbortController();
        const onConnect = () => {
            setSocketConnected(true);
            reconcilePickups(controller.signal);
            reconcileDriverLocations(controller.signal);
            reconcileDrivers(controller.signal);
        };
        const onDisconnect = () => setSocketConnected(false);
        setSocketConnected(Boolean(socket.connected));
        socket.on('connect', onConnect);
        socket.on('disconnect', onDisconnect);

        return () => {
            controller.abort();
            socket.off?.('connect', onConnect);
            socket.off?.('disconnect', onDisconnect);
        };
    }, [reconcileDriverLocations, reconcileDrivers, reconcilePickups]);

    useEffect(() => {
        if (!admin?.id) return undefined;

        const channel = echo.channel('np-admin-pickups-navi');
        const handlePickupEvent = (event) => {
            if (!event?.pickup) return;
            const pickup = event.pickup;

            if (!isNaviWhitelabelRecord(pickup)) {
                if (pickup.channel_id) {
                    pendingPickupAddedAtRef.current.delete(pickup.channel_id);
                    setPendingPickupChannels((current) => current.filter((channelName) => channelName !== pickup.channel_id));
                }
                pickupEventVersionRef.current.set(recordIdKey(pickup.id), ++pickupEventSequenceRef.current);
                setPickups((current) => upsertPickup(current, pickup));
                return;
            }

            const statusId = Number(pickup.delivery_status?.id ?? pickup.delivery_status_id);
            const terminal = statusId === 5 || statusId === 6;

            if (pickup.channel_id && !pickup.waiting_for_driver_at && !terminal) {
                if (!pendingPickupAddedAtRef.current.has(pickup.channel_id)) {
                    pendingPickupAddedAtRef.current.set(pickup.channel_id, Date.now());
                }
                setPendingPickupChannels((current) => current.includes(pickup.channel_id)
                    ? current
                    : [...current, pickup.channel_id]);
            }
            pickupEventVersionRef.current.set(recordIdKey(event.pickup.id), ++pickupEventSequenceRef.current);
            setPickups((current) => upsertPickup(current, event.pickup));
        };

        channel.listen('.App\\Events\\Admin\\PickupCreated', handlePickupEvent);
        channel.listen('.App\\Events\\Admin\\PickupUpdated', handlePickupEvent);
        channel.listen('.App\\Events\\Admin\\PickupAssignmentUpdated', handlePickupEvent);

        return () => echo.leave('np-admin-pickups-navi');
    }, [admin]);

    const pickupChannelKey = useMemo(() => Array.from(new Set([
        ...dispatchPickups.map((pickup) => pickup.channel_id).filter(Boolean),
        ...pendingPickupChannels,
    ])).sort().join('|'), [dispatchPickups, pendingPickupChannels]);

    useEffect(() => {
        if (!pickupChannelKey) return undefined;

        const channelNames = pickupChannelKey.split('|');
        const controller = new AbortController();
        const handlePickupEvent = (event) => {
            if (!event?.pickup) return;
            const pickup = event.pickup;

            if (!isNaviWhitelabelRecord(pickup)) {
                if (pickup.channel_id) {
                    pendingPickupAddedAtRef.current.delete(pickup.channel_id);
                    setPendingPickupChannels((current) => current.filter((channelName) => channelName !== pickup.channel_id));
                }
                pickupEventVersionRef.current.set(recordIdKey(pickup.id), ++pickupEventSequenceRef.current);
                setPickups((current) => upsertPickup(current, pickup));
                return;
            }

            const statusId = Number(pickup.delivery_status?.id ?? pickup.delivery_status_id);
            if (pickup.channel_id && (pickup.waiting_for_driver_at || statusId === 5 || statusId === 6)) {
                pendingPickupAddedAtRef.current.delete(pickup.channel_id);
                setPendingPickupChannels((current) => current.filter((channelName) => channelName !== pickup.channel_id));
            }
            pickupEventVersionRef.current.set(recordIdKey(event.pickup.id), ++pickupEventSequenceRef.current);
            setPickups((current) => upsertPickup(current, event.pickup));
        };

        channelNames.forEach((channelName) => {
            echo.channel(channelName).listen('.App\\Events\\Admin\\PickupUpdated', handlePickupEvent);
        });
        reconcilePickups(controller.signal);

        return () => {
            controller.abort();
            channelNames.forEach((channelName) => echo.leave(channelName));
        };
    }, [pickupChannelKey, reconcilePickups]);

    const driverChannelKey = useMemo(
        () => getDriverLocationSubscriptionIds(drivers).join('|'),
        [drivers]
    );

    useEffect(() => {
        if (!driverChannelKey) return undefined;

        setEchoAuthToken(token);
        const driverIds = driverChannelKey.split('|');
        const subscriptions = driverIds.map((driverId) => {
            const channelName = `np-driver-locations-${driverId}`;
            const handler = (event) => {
                const update = normalizeDriverLocationUpdate(event, driverId);
                if (!update) return;

                setDriverLocations((current) => mergeDriverLocations(current, {
                    [update.driverId]: update.location,
                }));
            };

            const presenceHandler = (event) => {
                const update = normalizeDriverPresenceUpdate(event, driverId);
                if (!update) return;
                setDriverPresence((current) => mergeDriverPresence(current, {
                    [update.driverId]: update.presence,
                }));
            };
            const channel = echo.private(channelName);
            channel.listen('.App\\Events\\DriverLocationUpdated', handler);
            channel.listen('.App\\Events\\DriverPresenceUpdated', presenceHandler);
            return {channel, channelName, handler, presenceHandler};
        });

        return () => subscriptions.forEach(({channel, channelName, handler, presenceHandler}) => {
            channel.stopListening('.App\\Events\\DriverLocationUpdated', handler);
            channel.stopListening('.App\\Events\\DriverPresenceUpdated', presenceHandler);
            echo.leave(channelName);
        });
    }, [driverChannelKey, token]);

    const activePickupsByDriver = useMemo(() => {
        return dispatchPickups.reduce((grouped, pickup) => {
            if (
                pickup.driver?.id === null
                || pickup.driver?.id === undefined
                || getDriverAvailability({available_to_deliver: 1}, pickup) !== 'busy'
            ) return grouped;

            const driverId = String(pickup.driver.id);
            const assignedPickups = grouped.get(driverId) || [];
            grouped.set(driverId, [...assignedPickups, pickup]);
            return grouped;
        }, new Map());
    }, [dispatchPickups]);

    const operationalDrivers = useMemo(() => drivers.filter(isDispatchDriver), [drivers]);

    const truckTypes = useMemo(() => {
        const types = [
            ...operationalDrivers.flatMap(getDriverTruckTypes),
            ...dispatchPickups.map((pickup) => pickup.truck_type).filter(isNaviWhitelabelRecord),
        ];
        const typesById = types.reduce((result, type) => {
            const id = String(type.id);
            const existing = result.get(id) || {};
            result.set(id, {
                ...existing,
                ...type,
                type: type.type || existing.type,
                icon: type.icon || existing.icon,
                title: type.title || existing.title,
            });
            return result;
        }, new Map());

        return Array.from(typesById.values())
            .sort((a, b) => (a.title || '').localeCompare(b.title || ''));
    }, [dispatchPickups, operationalDrivers]);

    const selectedDriver = operationalDrivers.find(
        (driver) => String(driver.id) === String(selectedDriverId)
    ) || null;
    const selectedDriverActivePickups = useMemo(() => (
        selectedDriver
            ? activePickupsByDriver.get(String(selectedDriver.id)) || []
            : []
    ), [activePickupsByDriver, selectedDriver]);
    const selectedDriverPickups = useMemo(() => (
        pickupSelectionSource === 'driver'
            ? getDriverAssignedPickups(dispatchPickups, selectedDriverId)
            : []
    ), [dispatchPickups, pickupSelectionSource, selectedDriverId]);
    const pickupMatchingSelectedId = dispatchPickups.find(
        (pickup) => String(pickup.id) === String(selectedPickupId)
    );
    const manuallySelectedPickupIdSet = useMemo(
        () => new Set(manuallySelectedPickupIds.map(String)),
        [manuallySelectedPickupIds]
    );
    const manuallySelectedPickups = useMemo(
        () => dispatchPickups.filter((pickup) => manuallySelectedPickupIdSet.has(String(pickup.id))),
        [dispatchPickups, manuallySelectedPickupIdSet]
    );
    const selectedPickup = pickupSelectionSource === 'driver'
        ? (
            pickupMatchingSelectedId && !hasPickupDriver(pickupMatchingSelectedId)
                ? pickupMatchingSelectedId
                : selectedDriverPickups.find((pickup) => String(pickup.id) === String(selectedPickupId))
                    || selectedDriverPickups[0]
                    || null
        )
        : pickupMatchingSelectedId || manuallySelectedPickups[0] || null;
    const selectedPickups = useMemo(() => (
        pickupSelectionSource === 'driver'
            ? uniqueById([selectedPickup, ...selectedDriverPickups].filter(Boolean))
            : uniqueById([selectedPickup, ...manuallySelectedPickups].filter(Boolean))
    ), [manuallySelectedPickups, pickupSelectionSource, selectedDriverPickups, selectedPickup]);
    const selectedDriverRoutePickups = useMemo(() => {
        if (!selectedDriver) return [];

        const selectedAssignedPickups = getSelectedAssignedPickups(
            selectedDriverActivePickups,
            selectedPickups
        );
        const driverAvailability = getDriverAvailability(
            selectedDriver,
            selectedDriverActivePickups[0]
        );
        const proposedPickups = driverAvailability === 'available'
            ? selectedPickups.filter((pickup) => (
                !hasPickupDriver(pickup) && isDriverCompatible(selectedDriver, pickup)
            ))
            : [];

        return uniqueById([...selectedAssignedPickups, ...proposedPickups]);
    }, [selectedDriver, selectedDriverActivePickups, selectedPickups]);
    const selectedPickupIds = useMemo(
        () => selectedPickups.map((pickup) => pickup.id),
        [selectedPickups]
    );
    const selectedPickupIdSet = useMemo(
        () => new Set(selectedPickupIds.map(String)),
        [selectedPickupIds]
    );
    const primarySelectedPickupId = selectedPickup?.id ?? null;
    const selectedPickupDriverLocation = selectedPickup?.driver?.id
        ? driverLocations[selectedPickup.driver.id]
        : null;
    const selectedPickupDriverPresence = driverPresence[selectedPickup?.driver?.id];
    const selectedPickupDriverIsOnline = isDriverOnline(selectedPickupDriverPresence, now);

    const filteredDrivers = useMemo(() => {
        const query = driverSearch.trim().toLowerCase();
        const results = operationalDrivers.filter((driver) => {
            const assignedPickup = activePickupsByDriver.get(String(driver.id))?.[0];
            const location = driverLocations[driver.id];
            const truckTypeMatch = !truckTypeFilter || getDriverTruckTypes(driver)
                .some((type) => String(type.id) === truckTypeFilter);
            const statusMatch = matchesDriverStatusFilter(
                driverStatusFilter,
                driver,
                assignedPickup,
                location,
                now,
                driverPresence[driver.id]
            );
            const searchText = [
                driver.id,
                driver.name,
                driver.phone_num,
                driver.company?.business_name,
                ...getDriverTruckTypes(driver).map((type) => type.title),
            ].filter(Boolean).join(' ').toLowerCase();

            return truckTypeMatch && statusMatch && (!query || searchText.includes(query));
        });

        return results.sort((a, b) => {
            if (selectedPickup && !hasPickupDriver(selectedPickup)) {
                const compatibilityDifference = Number(isDriverCompatible(b, selectedPickup)) - Number(isDriverCompatible(a, selectedPickup));
                if (compatibilityDifference) return compatibilityDifference;
            }

            const locationDifference = Number(Boolean(driverLocations[b.id])) - Number(Boolean(driverLocations[a.id]));
            if (locationDifference) return locationDifference;
            return (a.name || '').localeCompare(b.name || '');
        });
    }, [
        activePickupsByDriver,
        driverLocations,
        driverPresence,
        driverSearch,
        driverStatusFilter,
        now,
        operationalDrivers,
        selectedPickup,
        truckTypeFilter,
    ]);

    const filteredPickups = useMemo(() => {
        const query = pickupSearch.trim().toLowerCase();

        return dispatchPickups.filter((pickup) => {
            const pickupTruckTypeId = pickup.truck_type?.id ?? pickup.truck_type_id;
            const truckTypeMatch = !truckTypeFilter
                || String(pickupTruckTypeId) === truckTypeFilter;
            const statusMatch = pickupStatusFilter === 'all'
                || (pickupStatusFilter === 'unassigned' && !hasPickupDriver(pickup))
                || (pickupStatusFilter === 'active' && hasPickupDriver(pickup));
            const scheduledMatch = !scheduledOnly || isScheduledPickup(pickup);
            const searchText = [
                pickup.id,
                pickup.origin_address,
                pickup.dropoff_address,
                pickup.user?.full_name,
                pickup.driver?.name,
                isNaviWhitelabelRecord(pickup.truck_type) ? pickup.truck_type.title : null,
            ].filter(Boolean).join(' ').toLowerCase();

            return truckTypeMatch && statusMatch && scheduledMatch && (!query || searchText.includes(query));
        });
    }, [dispatchPickups, pickupSearch, pickupStatusFilter, scheduledOnly, truckTypeFilter]);

    const displayedPickups = filteredPickups;

    const mapDrivers = useMemo(() => uniqueById([
        ...filteredDrivers,
        selectedDriver,
    ].filter(Boolean)), [filteredDrivers, selectedDriver]);

    const mapPickups = useMemo(() => uniqueById([
        ...filteredPickups,
        ...selectedPickups,
        ...selectedDriverRoutePickups,
    ]), [filteredPickups, selectedDriverRoutePickups, selectedPickups]);

    const onlineDriverCount = operationalDrivers.filter((driver) => isDriverOnline(driverPresence[driver.id], now)).length;
    const availableDriverCount = operationalDrivers.filter((driver) => (
        getDriverAvailability(driver, activePickupsByDriver.get(String(driver.id))?.[0]) === 'available'
    )).length;
    const driverAvailabilityById = useMemo(() => new Map(
        operationalDrivers.map((driver) => [
            String(driver.id),
            getDriverAvailability(driver, activePickupsByDriver.get(String(driver.id))?.[0]),
        ])
    ), [activePickupsByDriver, operationalDrivers]);
    const unassignedCount = dispatchPickups.filter((pickup) => !hasPickupDriver(pickup)).length;
    const scheduledPickupCount = dispatchPickups.filter(isScheduledPickup).length;
    const deliveryFilterScope = scheduledOnly
        ? dispatchPickups.filter(isScheduledPickup)
        : dispatchPickups;
    const deliveryFilterUnassignedCount = deliveryFilterScope.filter(
        (pickup) => !hasPickupDriver(pickup)
    ).length;

    const mobileDispatchLayout = isMobileDispatchViewport();
    const dispatchPageWidth = dispatchViewport.width || dispatchPageElement?.clientWidth || 0;
    const dispatchPageHeight = dispatchViewport.height
        || dispatchPageElement?.clientHeight
        || (typeof window === 'undefined' ? 700 : window.innerHeight || 700);
    const baseDriversPanelWidth = clampLayoutSize(
        dispatchLayout.driversWidth,
        SIDEBAR_MIN_WIDTH,
        SIDEBAR_MAX_WIDTH
    );
    const baseDeliveriesPanelWidth = clampLayoutSize(
        dispatchLayout.deliveriesWidth,
        SIDEBAR_MIN_WIDTH,
        SIDEBAR_MAX_WIDTH
    );
    let driversPanelWidth = baseDriversPanelWidth;
    let deliveriesPanelWidth = baseDeliveriesPanelWidth;

    if (dispatchPageWidth && driversOpen && deliveriesOpen) {
        const availablePanelWidth = Math.max(
            SIDEBAR_MIN_WIDTH * 2,
            dispatchPageWidth - MIN_MAP_WORKSPACE_WIDTH
        );
        const flexibleWidth = (driversPanelWidth - SIDEBAR_MIN_WIDTH)
            + (deliveriesPanelWidth - SIDEBAR_MIN_WIDTH);

        if (driversPanelWidth + deliveriesPanelWidth > availablePanelWidth && flexibleWidth > 0) {
            const flexibleScale = Math.max(
                0,
                (availablePanelWidth - (SIDEBAR_MIN_WIDTH * 2)) / flexibleWidth
            );
            driversPanelWidth = Math.round(
                SIDEBAR_MIN_WIDTH + ((driversPanelWidth - SIDEBAR_MIN_WIDTH) * flexibleScale)
            );
            deliveriesPanelWidth = Math.min(
                baseDeliveriesPanelWidth,
                availablePanelWidth - driversPanelWidth
            );
        }
    } else if (dispatchPageWidth && driversOpen) {
        driversPanelWidth = clampLayoutSize(
            driversPanelWidth,
            SIDEBAR_MIN_WIDTH,
            Math.max(SIDEBAR_MIN_WIDTH, dispatchPageWidth - MIN_MAP_WORKSPACE_WIDTH)
        );
    } else if (dispatchPageWidth && deliveriesOpen) {
        deliveriesPanelWidth = clampLayoutSize(
            deliveriesPanelWidth,
            SIDEBAR_MIN_WIDTH,
            Math.max(SIDEBAR_MIN_WIDTH, dispatchPageWidth - MIN_MAP_WORKSPACE_WIDTH)
        );
    }

    const driversPanelMaxWidth = dispatchPageWidth
        ? clampLayoutSize(
            dispatchPageWidth
                - (deliveriesOpen ? deliveriesPanelWidth : 0)
                - MIN_MAP_WORKSPACE_WIDTH,
            SIDEBAR_MIN_WIDTH,
            SIDEBAR_MAX_WIDTH
        )
        : SIDEBAR_MAX_WIDTH;
    const deliveriesPanelMaxWidth = dispatchPageWidth
        ? clampLayoutSize(
            dispatchPageWidth
                - (driversOpen ? driversPanelWidth : 0)
                - MIN_MAP_WORKSPACE_WIDTH,
            SIDEBAR_MIN_WIDTH,
            SIDEBAR_MAX_WIDTH
        )
        : SIDEBAR_MAX_WIDTH;

    const viewportHeight = typeof window === 'undefined' ? 700 : window.innerHeight || 700;
    const routeWorkspaceMaxHeight = Math.max(
        ROUTE_WORKSPACE_MIN_HEIGHT,
        Math.floor(Math.min(
            ROUTE_WORKSPACE_MAX_HEIGHT,
            viewportHeight * 0.54,
            dispatchPageHeight - routeWorkspaceBottom - 58
        ))
    );
    const routeWorkspaceHeight = clampLayoutSize(
        dispatchLayout.routeHeight,
        ROUTE_WORKSPACE_MIN_HEIGHT,
        routeWorkspaceMaxHeight
    );
    const centralWorkspaceLeft = driversOpen ? driversPanelWidth : 0;
    const centralWorkspaceRight = deliveriesOpen ? deliveriesPanelWidth : 0;
    const centralWorkspaceWidth = dispatchPageWidth
        ? Math.max(0, dispatchPageWidth - centralWorkspaceLeft - centralWorkspaceRight)
        : 0;
    const dispatchTopbarStyle = !mobileDispatchLayout && centralWorkspaceWidth
        ? {
            left: centralWorkspaceLeft + (centralWorkspaceWidth / 2),
            right: 'auto',
            width: Math.max(0, Math.min(540, centralWorkspaceWidth - 20)),
            minWidth: 0,
            transform: 'translateX(-50%)',
        }
        : undefined;

    const beginResize = (kind, event, startSize, maximum) => {
        const touch = event.touches?.[0];
        if (mobileDispatchLayout || (!touch && event.button !== 0)) return;

        event.preventDefault();
        event.currentTarget.focus();
        const pointer = touch || event;
        setActiveResize({
            kind,
            sizeKey: kind === 'drivers'
                ? 'driversWidth'
                : kind === 'deliveries' ? 'deliveriesWidth' : 'routeHeight',
            startX: pointer.clientX,
            startY: pointer.clientY,
            startSize,
            minimum: kind === 'route' ? ROUTE_WORKSPACE_MIN_HEIGHT : SIDEBAR_MIN_WIDTH,
            maximum,
            inputType: touch ? 'touch' : 'mouse',
        });
    };

    const resizeWithKeyboard = (kind, event, currentSize, maximum) => {
        const minimum = kind === 'route' ? ROUTE_WORKSPACE_MIN_HEIGHT : SIDEBAR_MIN_WIDTH;
        const sizeKey = kind === 'drivers'
            ? 'driversWidth'
            : kind === 'deliveries' ? 'deliveriesWidth' : 'routeHeight';
        const directionByKey = kind === 'drivers'
            ? {ArrowLeft: -1, ArrowRight: 1}
            : kind === 'deliveries'
                ? {ArrowLeft: 1, ArrowRight: -1}
                : {ArrowUp: 1, ArrowDown: -1};
        let nextSize;

        if (event.key === 'Home') nextSize = minimum;
        else if (event.key === 'End') nextSize = maximum;
        else if (directionByKey[event.key]) {
            const step = event.shiftKey ? RESIZE_KEYBOARD_STEP * 2 : RESIZE_KEYBOARD_STEP;
            nextSize = currentSize + (directionByKey[event.key] * step);
        } else return;

        event.preventDefault();
        const clampedSize = clampLayoutSize(nextSize, minimum, maximum);
        setDispatchLayout((current) => (
            current[sizeKey] === clampedSize ? current : {...current, [sizeKey]: clampedSize}
        ));
    };

    const openSidebar = (side) => {
        const compact = isCompactViewport();
        if (side === 'drivers') {
            setDriversOpen(true);
            if (compact) setDeliveriesOpen(false);
        } else {
            setDeliveriesOpen(true);
            if (compact) setDriversOpen(false);
        }
    };

    const selectDriver = (driverId) => {
        const selection = getDriverPickupSelection(dispatchPickups, driverId, selectedPickupId);

        setSelectedDriverId(driverId);
        setPickupSelectionSource(selection.source);
        setSelectedPickupId(selection.pickupId);
        setDriverFocusRequest((request) => request + 1);
        setAssignmentMessage(null);
        openSidebar(selection.assignedPickups.length ? 'deliveries' : 'drivers');
    };

    const selectPickup = (pickupId) => {
        const pickup = dispatchPickups.find((item) => String(item.id) === String(pickupId));
        if (!pickup) return;

        const pickupIdKey = String(pickup.id);
        const wasSelected = selectedPickupIds.some((selectedId) => String(selectedId) === pickupIdKey);
        const nextSelectedPickupIds = togglePickupSelection(selectedPickupIds, pickup.id);
        const primaryWasRemoved = wasSelected && String(selectedPickupId) === pickupIdKey;
        const nextPrimaryPickupId = primaryWasRemoved
            ? nextSelectedPickupIds[0] ?? null
            : wasSelected ? selectedPickupId : pickup.id;
        const nextPrimaryPickup = dispatchPickups.find(
            (item) => String(item.id) === String(nextPrimaryPickupId)
        );

        setPickupSelectionSource('pickup');
        setManuallySelectedPickupIds(nextSelectedPickupIds);
        setSelectedPickupId(nextPrimaryPickupId);

        const driverSourcePickup = wasSelected ? nextPrimaryPickup : pickup;
        if (driverSourcePickup?.driver?.id) setSelectedDriverId(driverSourcePickup.driver.id);
        else if (driverSourcePickup && hasPickupDriver(driverSourcePickup)) setSelectedDriverId(null);
        setAssignmentMessage(null);
        openSidebar('deliveries');
    };

    const clearSelectedPickups = () => {
        setManuallySelectedPickupIds([]);
        setPickupSelectionSource('pickup');
        setSelectedPickupId(null);
        setAssignmentMessage(null);
    };

    const clearSelectedDriver = () => {
        setSelectedDriverId(null);
        setPickupSelectionSource('pickup');
    };

    const resetFilters = () => {
        setDriverSearch('');
        setDriverStatusFilter('all');
        setTruckTypeFilter('');
    };

    const assignDriver = async () => {
        if (!selectedPickup || !selectedDriver || hasPickupDriver(selectedPickup) || assigning) return;

        setAssigning(true);
        setAssignmentMessage(null);

        try {
            const response = await axios.post(
                `${API_URL}/api/admin/pickups/${selectedPickup.id}/claim`,
                {driver_id: selectedDriver.id, navi_only: true},
                {headers: authHeaders(token)}
            );
            const updatedPickup = response.data?.pickup;
            if (updatedPickup) {
                pickupEventVersionRef.current.set(recordIdKey(updatedPickup.id), ++pickupEventSequenceRef.current);
                setPickups((current) => upsertPickup(current, updatedPickup));
            }
            setAssignmentMessage({type: 'success', text: response.data?.message || 'Driver assigned successfully.'});
        } catch (error) {
            if (error?.response?.status === 401) {
                handleUnauthorized();
                return;
            }
            setAssignmentMessage({
                type: 'error',
                text: error?.response?.data?.message || 'This delivery could not be assigned. Please try again.',
            });
        } finally {
            setAssigning(false);
        }
    };

    const compatible = selectedDriver && selectedPickup
        ? isDriverCompatible(selectedDriver, selectedPickup)
        : false;
    const selectedDriverPickup = selectedDriverActivePickups[0] || null;
    const selectedDriverAvailability = selectedDriver
        ? getDriverAvailability(selectedDriver, selectedDriverPickup)
        : null;
    const selectedDriverLocation = selectedDriver ? driverLocations[selectedDriver.id] : null;
    const scheduledDriverRoutePlan = useMemo(() => buildDriverRoutePlan(
        selectedDriverRoutePickups,
        {now: routePlanStartAt, serviceMinutes: routeServiceMinutes}
    ), [routePlanStartAt, routeServiceMinutes, selectedDriverRoutePickups]);
    const routeStopSignature = useMemo(
        () => scheduledDriverRoutePlan.map((stop) => stop.key).join('|'),
        [scheduledDriverRoutePlan]
    );

    useEffect(() => {
        setRoutePlanStartAt(Date.now());
        setRouteStopOrder([]);
        setRoutePlanMode('scheduled');
        setRoutePlanNotice('');
        setRouteTravelMetrics((current) => (
            current
            && String(current.driverId) === String(selectedDriverId)
            && current.stopOrderKey === routeStopSignature
                ? current
                : null
        ));
    }, [routeStopSignature, selectedDriverId]);

    const orderedDriverRoutePreview = useMemo(() => buildDriverRoutePlan(
        selectedDriverRoutePickups,
        {
            now: routePlanStartAt,
            serviceMinutes: routeServiceMinutes,
            stopOrder: routeStopOrder,
        }
    ), [routePlanStartAt, routeServiceMinutes, routeStopOrder, selectedDriverRoutePickups]);
    const routeStopOrderKey = useMemo(
        () => orderedDriverRoutePreview.map((stop) => stop.key).join('|'),
        [orderedDriverRoutePreview]
    );
    const routeMetricsMatchCurrentDriver = routeTravelMetrics
        && String(routeTravelMetrics.driverId) === String(selectedDriverId);
    const routeTravelDurations = useMemo(() => (
        routeMetricsMatchCurrentDriver && routeTravelMetrics.stopOrderKey === routeStopOrderKey
            ? routeTravelMetrics.legDurationsMinutes || []
            : []
    ), [routeMetricsMatchCurrentDriver, routeStopOrderKey, routeTravelMetrics]);
    const selectedDriverRoutePlan = useMemo(() => buildDriverRoutePlan(
        selectedDriverRoutePickups,
        {
            now: routePlanStartAt,
            serviceMinutes: routeServiceMinutes,
            stopOrder: routeStopOrder,
            travelDurationsMinutes: routeTravelDurations,
        }
    ), [
        routePlanStartAt,
        routeServiceMinutes,
        routeStopOrder,
        routeTravelDurations,
        selectedDriverRoutePickups,
    ]);
    const selectedDriverRouteMetrics = useMemo(
        () => getRoutePlanMetrics(selectedDriverRoutePlan, selectedDriverLocation),
        [selectedDriverLocation, selectedDriverRoutePlan]
    );
    const currentRoadMetrics = routeMetricsMatchCurrentDriver
        && routeTravelMetrics.stopOrderKey === routeStopOrderKey
        ? routeTravelMetrics
        : null;
    const routeTimingLoading = !currentRoadMetrics || currentRoadMetrics.source === 'loading';
    const routeTimingReady = currentRoadMetrics?.source === 'roads';
    const routeTimingComplete = routeTimingReady && Boolean(selectedDriverLocation);
    const handleRouteMetrics = useCallback((metrics) => {
        setRouteTravelMetrics((current) => {
            const nextMetrics = metrics ? {...metrics, driverId: selectedDriverId} : metrics;
            const currentLegs = current?.legDurationsMinutes?.join('|') || '';
            const nextLegs = nextMetrics?.legDurationsMinutes?.join('|') || '';
            if (
                String(current?.driverId) === String(nextMetrics?.driverId)
                && current?.stopOrderKey === nextMetrics?.stopOrderKey
                && current?.source === nextMetrics?.source
                && current?.distanceKm === nextMetrics?.distanceKm
                && current?.reason === nextMetrics?.reason
                && currentLegs === nextLegs
            ) return current;

            return nextMetrics;
        });
    }, [selectedDriverId]);

    // Road metrics are keyed by driver and stop order. Retain them here so an
    // idempotent optimize/reset, or a cached map result published while the
    // selection effect runs, keeps valid estimates. Mismatched metrics stay
    // hidden until the map publishes values for the current route.
    const setManualRouteOrder = (nextStops) => {
        if (nextStops === selectedDriverRoutePlan) {
            setRoutePlanNotice('Pickup stops must stay before their matching drop-offs.');
            return false;
        }

        setRouteStopOrder(nextStops.map((stop) => stop.key));
        setRoutePlanMode('manual');
        setRoutePlanNotice('');
        return true;
    };

    const movePlannedStop = (sourceKey, targetKey, placement = 'before') => {
        const nextStops = moveRouteStop(selectedDriverRoutePlan, sourceKey, targetKey, placement);
        return setManualRouteOrder(nextStops);
    };

    const optimizeSelectedDriverRoute = () => {
        setRouteStopOrder(optimizeRouteStopOrder(scheduledDriverRoutePlan, selectedDriverLocation));
        setRoutePlanMode('optimized');
        setRoutePlanNotice('Route optimized by stop proximity while keeping every pickup before its drop-off.');
        setRouteFitRequest((request) => request + 1);
    };

    const resetSelectedDriverRoute = () => {
        setRoutePlanStartAt(Date.now());
        setRouteStopOrder([]);
        setRoutePlanMode('scheduled');
        setRoutePlanNotice('Restored scheduled delivery order.');
    };

    const selectedDriverLocationIsLive = isLocationFresh(selectedDriverLocation, now);
    const selectedDriverPresence = driverPresence[selectedDriver?.id];
    const selectedDriverIsOnline = isDriverOnline(selectedDriverPresence, now);
    const selectedDriverVehicleLabel = selectedDriver
        ? getDriverTruckTypes(selectedDriver).map((type) => type.title).filter(Boolean).join(', ')
        : '';
    const assignmentDisabled = !canAssign
        || !selectedPickup
        || !selectedDriver
        || hasPickupDriver(selectedPickup)
        || !compatible
        || selectedDriverAvailability !== 'available'
        || assigning;
    const routeWorkspaceVisible = Boolean(selectedDriver && selectedDriverRoutePickups.length > 0);

    if (loading) {
        return (
            <div className="dispatch-page dispatch-initial-loading">
                <span className="dispatch-map-spinner"/>
                <h1>Opening Dispatch Center</h1>
                <p>Loading drivers, active deliveries, and live channels...</p>
            </div>
        );
    }

    return (
        <div
            className={`dispatch-page ${driversOpen ? 'drivers-panel-open' : ''} ${deliveriesOpen ? 'deliveries-panel-open' : ''} ${routeWorkspaceVisible ? 'route-workspace-open' : ''} ${activeResize ? `resizing-${activeResize.kind === 'route' ? 'height' : 'width'}` : ''}`}
            data-testid="dispatch-page"
            ref={setDispatchPageElement}
            style={dispatchViewport.height ? {height: dispatchViewport.height} : undefined}
        >
            <div className="dispatch-map-layer">
                <GoogleMapsProvider>
                    <DispatchMap
                        drivers={mapDrivers}
                        driverLocations={driverLocations}
                        driverPresence={driverPresence}
                        driverAvailabilityById={driverAvailabilityById}
                        pickups={mapPickups}
                        selectedDriverId={selectedDriverId}
                        selectedPickupIds={selectedPickupIds}
                        onSelectDriver={selectDriver}
                        onSelectPickup={selectPickup}
                        fitRequest={fitRequest}
                        routeFitRequest={routeFitRequest}
                        driverFocusRequest={driverFocusRequest}
                        plannedRouteStops={selectedDriverRoutePlan}
                        onRouteMetrics={handleRouteMetrics}
                        now={now}
                        fitPadding={mapPadding}
                    />
                </GoogleMapsProvider>
            </div>

            <header className="dispatch-topbar" style={dispatchTopbarStyle}>
                <div className="dispatch-topbar-title">
                    <span className="dispatch-brand-icon"><Sliders size={18}/></span>
                    <span>
                        <strong>Dispatch Center</strong>
                        <small className={socketConnected ? 'connected' : 'disconnected'}>
                            <span className="dispatch-live-dot"/>
                            {socketConnected ? 'Live updates' : 'Reconnecting'}
                        </small>
                    </span>
                </div>
                <div className="dispatch-stat-chips" aria-label="Dispatch summary">
                    <span><Broadcast size={13}/><b>{onlineDriverCount}</b> online</span>
                    <span><Truck size={14}/><b>{availableDriverCount}</b> available</span>
                    <span className={unassignedCount ? 'needs-attention' : ''}><BoxSeam size={13}/><b>{unassignedCount}</b> unassigned</span>
                </div>
                <div className="dispatch-topbar-actions">
                    <button
                        type="button"
                        onClick={() => setFitRequest((request) => request + 1)}
                        title="Fit all visible drivers and deliveries"
                        aria-label="Fit map to results"
                    >
                        <ArrowsFullscreen size={16}/>
                    </button>
                    <button
                        type="button"
                        onClick={() => loadData(true)}
                        disabled={refreshing}
                        title="Refresh dispatch data"
                        aria-label="Refresh dispatch data"
                    >
                        <ArrowClockwise size={17} className={refreshing ? 'spin' : ''}/>
                    </button>
                </div>
            </header>

            {(loadError || locationError) && (
                <div className="dispatch-alert dispatch-alert-error" role="alert">
                    <span>{loadError || locationError}</span>
                    <button
                        type="button"
                        onClick={() => (loadError ? loadData(true) : reconcileDriverLocations())}
                    >
                        Try again
                    </button>
                </div>
            )}

            {assignmentMessage && (
                <div className={`dispatch-alert dispatch-alert-${assignmentMessage.type}`} role="status">
                    {assignmentMessage.type === 'success' && <CheckCircleFill size={15}/>} 
                    <span>{assignmentMessage.text}</span>
                    <button type="button" className="icon-only" onClick={() => setAssignmentMessage(null)} aria-label="Dismiss message">
                        <X size={17}/>
                    </button>
                </div>
            )}

            <aside
                id="dispatch-drivers-panel"
                ref={driversSidebarRef}
                className={`dispatch-sidebar dispatch-drivers-sidebar ${driversOpen ? 'open' : 'closed'}`}
                aria-label="Drivers"
                style={mobileDispatchLayout ? undefined : {width: driversPanelWidth}}
            >
                <div className="dispatch-sidebar-header">
                    <div>
                        <span className="dispatch-sidebar-eyebrow">Fleet</span>
                        <h2>Drivers <span>{filteredDrivers.length}</span></h2>
                    </div>
                    <button type="button" className="dispatch-sidebar-close" onClick={() => setDriversOpen(false)} aria-label="Hide drivers panel">
                        <ChevronLeft size={19}/>
                    </button>
                </div>

                <div className="dispatch-sidebar-controls">
                    <label className="dispatch-search">
                        <Search size={15}/>
                        <input
                            type="search"
                            value={driverSearch}
                            onChange={(event) => setDriverSearch(event.target.value)}
                            placeholder="Search driver or vehicle"
                            aria-label="Search drivers"
                        />
                        {driverSearch && (
                            <button type="button" onClick={() => setDriverSearch('')} aria-label="Clear driver search"><X size={15}/></button>
                        )}
                    </label>

                    <div className="dispatch-segmented dispatch-driver-filters" aria-label="Filter drivers">
                        {DRIVER_STATUS_FILTERS.map(([value, label]) => (
                            <button
                                key={value}
                                type="button"
                                className={driverStatusFilter === value ? 'active' : ''}
                                onClick={() => setDriverStatusFilter(value)}
                                aria-pressed={driverStatusFilter === value}
                            >
                                {label}
                            </button>
                        ))}
                    </div>

                    <div className="dispatch-truck-filter">
                        <div className="dispatch-truck-filter-heading">
                            <span>Vehicle type</span>
                            {(driverStatusFilter !== 'all' || truckTypeFilter) && (
                                <button type="button" onClick={resetFilters}>Clear filters</button>
                            )}
                        </div>
                        <div className="dispatch-truck-filter-options" role="group" aria-label="Filter drivers and deliveries by vehicle type">
                            <button
                                type="button"
                                className={!truckTypeFilter ? 'active' : ''}
                                onClick={() => setTruckTypeFilter('')}
                                aria-pressed={!truckTypeFilter}
                                title="Every vehicle type"
                            >
                                <TruckTypeIcon/>
                                <span>All</span>
                            </button>
                            {truckTypes.map((type) => {
                                const value = String(type.id);
                                return (
                                    <button
                                        key={type.id}
                                        type="button"
                                        className={truckTypeFilter === value ? 'active' : ''}
                                        onClick={() => setTruckTypeFilter(value)}
                                        aria-pressed={truckTypeFilter === value}
                                        title={type.title}
                                    >
                                        <TruckTypeIcon truckType={type}/>
                                        <span>{type.title}</span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>

                <div className="dispatch-sidebar-list dispatch-driver-list">
                    {filteredDrivers.map((driver) => {
                        const location = driverLocations[driver.id];
                        const presence = driverPresence[driver.id];
                        const online = isDriverOnline(presence, now);
                        const assignedPickups = activePickupsByDriver.get(String(driver.id)) || [];
                        const assignedPickup = assignedPickups[0];
                        const availability = getDriverAvailability(driver, assignedPickup);
                        const isSelected = String(driver.id) === String(selectedDriverId);
                        const locationStatus = isLocationFresh(location, now)
                            ? 'live'
                            : location ? 'stale' : 'offline';
                        const driverTruckTypes = getDriverTruckTypes(driver);
                        const vehicleLabel = driverTruckTypes
                            .map((type) => type.title || type.type)
                            .filter(Boolean)
                            .join(', ') || 'Vehicle not assigned';
                        const isCompatible = selectedPickup && !hasPickupDriver(selectedPickup)
                            ? isDriverCompatible(driver, selectedPickup)
                            : null;

                        return (
                            <button
                                type="button"
                                key={driver.id}
                                className={`dispatch-driver-card ${isSelected ? 'selected' : ''} ${isCompatible === false ? 'incompatible' : ''}`}
                                onClick={() => selectDriver(driver.id)}
                                aria-pressed={isSelected}
                            >
                                <span className="dispatch-driver-avatar">
                                    {getDriverInitials(driver.name)}
                                    {driver.profile_photo && (
                                        <img
                                            src={driver.profile_photo}
                                            alt=""
                                            onError={(event) => {
                                                event.currentTarget.hidden = true;
                                            }}
                                        />
                                    )}
                                    <i className={online ? 'live' : 'offline'}/>
                                </span>
                                <span className="dispatch-driver-card-body">
                                    <span className="dispatch-driver-name-row">
                                        <strong>#{driver.id} {driver.name || 'Driver'}</strong>
                                        <em className={`dispatch-driver-work-state ${availability === 'busy' ? 'active' : ''}`}>
                                            {availability === 'busy'
                                                ? assignedPickups.length > 1
                                                    ? `${assignedPickups.length} deliveries`
                                                    : 'On delivery'
                                                : 'No active delivery'}
                                        </em>
                                    </span>
                                    <span className="dispatch-driver-vehicle">
                                        <TruckTypeIcon truckType={driverTruckTypes[0]}/>
                                        <span>{vehicleLabel}</span>
                                        {isCompanyEmployeeDriver(driver) && (
                                            <small>{driver.company?.business_name || 'Company employee'}</small>
                                        )}
                                    </span>
                                    <span className="dispatch-driver-location dispatch-driver-presence">
                                        <Broadcast aria-hidden="true"/>
                                        <b className={online ? 'live' : 'offline'}>{getDriverConnectionLabel(presence, now)}</b>
                                        <em className={`dispatch-driver-availability-label ${availability}`}>
                                            {availability === 'busy' ? `#${assignedPickup.id}` : availability}
                                        </em>
                                        {isCompatible !== null && (
                                            <i className={`dispatch-driver-match ${isCompatible ? 'match' : 'mismatch'}`}>
                                                {isCompatible ? 'Match' : 'No match'}
                                            </i>
                                        )}
                                    </span>
                                    <span className="dispatch-driver-location">
                                        <GeoAltFill aria-hidden="true"/>
                                        <b className={locationStatus}>
                                            {locationStatus === 'live' ? 'Fresh GPS' : locationStatus === 'stale' ? 'Stale GPS' : 'No GPS'}
                                        </b>
                                        <span title={getDriverLocationLabel(location, now)}>{location ? getDriverLocationLabel(location, now) : 'Awaiting location'}</span>
                                    </span>
                                </span>
                            </button>
                        );
                    })}

                    {!filteredDrivers.length && (
                        <SidebarEmptyState
                            icon={<Truck size={22}/>}
                            title="No drivers found"
                            copy="Try clearing a filter or using another search."
                        />
                    )}
                </div>

                <div className="dispatch-sidebar-footer">
                    <span><i className={`dispatch-live-dot ${socketConnected ? '' : 'offline'}`}/>{socketConnected ? 'Location stream connected' : 'Location stream reconnecting'}</span>
                    {lastSync && <small>Synced {formatRelativeTime(lastSync, now)}</small>}
                </div>

                <div className={`dispatch-driver-selection-panel ${selectedDriver ? 'has-selection' : ''}`}>
                    {!selectedDriver ? (
                        <div className="dispatch-assignment-guide">
                            <span><Truck size={17}/></span>
                            <p><strong>Select a driver</strong><small>View location, availability, and active deliveries.</small></p>
                        </div>
                    ) : (
                        <>
                            <div className="dispatch-assignment-heading">
                                <span>
                                    <small>Selected driver</small>
                                    <strong>{selectedDriver.name || `Driver #${selectedDriver.id}`}</strong>
                                </span>
                                <button type="button" onClick={clearSelectedDriver} aria-label="Clear selected driver">Clear</button>
                            </div>
                            <div className="dispatch-tracking-card dispatch-selected-driver-card">
                                <span className="dispatch-driver-avatar small">
                                    {selectedDriver.profile_photo
                                        ? <img src={selectedDriver.profile_photo} alt=""/>
                                        : getDriverInitials(selectedDriver.name)}
                                    <i className={selectedDriverIsOnline ? 'live' : 'offline'}/>
                                </span>
                                <span>
                                    <small>
                                        {selectedDriverAvailability === 'busy'
                                            ? selectedDriverActivePickups.length > 1
                                                ? `${selectedDriverActivePickups.length} active deliveries`
                                                : `Delivery #${selectedDriverPickup?.id}`
                                            : selectedDriverAvailability === 'available'
                                                ? 'Available to deliver'
                                                : 'Not available'}
                                    </small>
                                    <strong>{selectedDriverVehicleLabel || `Driver #${selectedDriver.id}`}</strong>
                                    <small>{getDriverLocationLabel(selectedDriverLocation, now)}</small>
                                </span>
                                <em className={selectedDriverIsOnline ? '' : 'offline'}>
                                    <Broadcast size={12}/>
                                    {getDriverConnectionLabel(selectedDriverPresence, now)}
                                </em>
                            </div>
                            {routeWorkspaceVisible && dispatchPageElement && createPortal(
                                <section
                                    ref={setRouteWorkspaceElement}
                                    className={`dispatch-route-workspace-panel ${routeWorkspaceCollapsed ? 'collapsed' : ''}`}
                                    aria-label="Route workspace"
                                    style={{
                                        '--dispatch-route-workspace-bottom': `${routeWorkspaceBottom}px`,
                                        left: mobileDispatchLayout ? 8 : mapPadding.left,
                                        right: mobileDispatchLayout ? 8 : mapPadding.right,
                                        height: !mobileDispatchLayout && !routeWorkspaceCollapsed
                                            ? routeWorkspaceHeight
                                            : undefined,
                                    }}
                                >
                                {!mobileDispatchLayout && !routeWorkspaceCollapsed && (
                                    <div
                                        className="dispatch-route-resize-handle"
                                        role="separator"
                                        tabIndex={0}
                                        aria-label="Resize route workspace"
                                        aria-controls="dispatch-route-plan"
                                        aria-orientation="horizontal"
                                        aria-valuemin={ROUTE_WORKSPACE_MIN_HEIGHT}
                                        aria-valuemax={routeWorkspaceMaxHeight}
                                        aria-valuenow={routeWorkspaceHeight}
                                        aria-valuetext={`${routeWorkspaceHeight} pixels high`}
                                        title="Drag or use the arrow keys to resize the Route workspace"
                                        onMouseDown={(event) => beginResize('route', event, routeWorkspaceHeight, routeWorkspaceMaxHeight)}
                                        onTouchStart={(event) => beginResize('route', event, routeWorkspaceHeight, routeWorkspaceMaxHeight)}
                                        onKeyDown={(event) => resizeWithKeyboard('route', event, routeWorkspaceHeight, routeWorkspaceMaxHeight)}
                                    >
                                        <GripHorizontal aria-hidden="true"/>
                                    </div>
                                )}
                                <section id="dispatch-route-plan" className="dispatch-route-plan" aria-label={`${selectedDriver.name || 'Driver'} route plan`}>
                                    <div className="dispatch-route-plan-heading">
                                        <span><Signpost2 size={14}/><strong>Route workspace</strong></span>
                                        <span className="dispatch-route-plan-heading-actions">
                                            <small className={routePlanMode}>{routePlanMode}</small>
                                            <button
                                                type="button"
                                                className="dispatch-route-workspace-toggle"
                                                onClick={() => setRouteWorkspaceCollapsed((collapsed) => !collapsed)}
                                                aria-label={`${routeWorkspaceCollapsed ? 'Expand' : 'Collapse'} route workspace`}
                                                aria-expanded={!routeWorkspaceCollapsed}
                                            >
                                                {routeWorkspaceCollapsed ? <ChevronUp/> : <ChevronDown/>}
                                            </button>
                                        </span>
                                    </div>
                                    {!routeWorkspaceCollapsed && (selectedDriverRoutePlan.length > 0 ? (
                                        <>
                                            <div className="dispatch-route-plan-toolbar" aria-label="Route planning controls">
                                                <button
                                                    type="button"
                                                    className="optimize"
                                                    onClick={optimizeSelectedDriverRoute}
                                                    disabled={selectedDriverRoutePlan.length < 2}
                                                    title="Optimize remaining stops by proximity"
                                                >
                                                    <Magic size={11}/> Optimize
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={resetSelectedDriverRoute}
                                                    title="Restore scheduled order and start from now"
                                                >
                                                    <ArrowCounterclockwise size={11}/> Reset
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setRouteFitRequest((request) => request + 1)}
                                                    title="Fit this route on the map"
                                                >
                                                    <ArrowsFullscreen size={11}/> Fit
                                                </button>
                                                <label className="dispatch-route-service-time" title="Average service time at every pickup and drop-off">
                                                    <Stopwatch size={11}/>
                                                    <select
                                                        value={routeServiceMinutes}
                                                        onChange={(event) => setRouteServiceMinutes(Number(event.target.value))}
                                                        aria-label="Service minutes per stop"
                                                    >
                                                        {[15, 30, 45, 60, 90, 120].map((minutes) => (
                                                            <option key={minutes} value={minutes}>{minutes} min</option>
                                                        ))}
                                                    </select>
                                                </label>
                                            </div>
                                            <div className="dispatch-route-plan-metrics" aria-label="Route summary">
                                                <span><b>{selectedDriverRouteMetrics.stopCount}</b><small>stops</small></span>
                                                <span>
                                                    <b>
                                                        {currentRoadMetrics?.source === 'loading'
                                                            ? '…'
                                                            : `${(
                                                                currentRoadMetrics?.source === 'roads'
                                                                    ? currentRoadMetrics.distanceKm
                                                                    : selectedDriverRouteMetrics.directDistanceKm
                                                            ).toFixed(1)} km`}
                                                    </b>
                                                    <small>{currentRoadMetrics?.source === 'roads' ? 'road' : 'direct'}</small>
                                                </span>
                                                <span>
                                                    <b>
                                                        {routeTimingLoading
                                                            ? '…'
                                                            : `${routeTimingComplete ? '' : '≥ '}${formatRouteDuration(selectedDriverRouteMetrics.totalDurationMinutes)}`}
                                                    </b>
                                                    <small>{routeTimingComplete ? 'total' : 'minimum'}</small>
                                                </span>
                                                <span>
                                                    <b>
                                                        {routeTimingLoading
                                                            ? '…'
                                                            : formatRoutePlanTime(selectedDriverRouteMetrics.plannedEndAt)}
                                                    </b>
                                                    <small>{routeTimingComplete ? 'finish' : 'earliest'}</small>
                                                </span>
                                            </div>
                                            <div className="dispatch-route-time-breakdown" aria-label="Route time breakdown">
                                                <span className="drive" title="Road driving time between the driver's location and all route stops">
                                                    <Truck size={11}/>
                                                    <b>
                                                        {routeTimingLoading
                                                            ? '…'
                                                            : routeTimingReady
                                                                ? formatRouteDuration(selectedDriverRouteMetrics.travelMinutes)
                                                                : '—'}
                                                    </b>
                                                    <small>{routeTimingComplete ? 'driving' : 'known drive'}</small>
                                                </span>
                                                <span className="service" title="Pickup and drop-off service time">
                                                    <Stopwatch size={11}/>
                                                    <b>{formatRouteDuration(selectedDriverRouteMetrics.serviceMinutes)}</b>
                                                    <small>service</small>
                                                </span>
                                                <span
                                                    className={`wait ${routeTimingReady && selectedDriverRouteMetrics.waitingMinutes > 0 ? 'has-delay' : ''}`}
                                                    title="Time spent waiting for a scheduled stop after the route has started"
                                                >
                                                    <ClockHistory size={11}/>
                                                    <b>
                                                        {routeTimingLoading
                                                            ? '…'
                                                            : routeTimingReady
                                                                ? formatRouteDuration(selectedDriverRouteMetrics.waitingMinutes)
                                                                : '—'}
                                                    </b>
                                                    <small>schedule wait</small>
                                                </span>
                                                <span
                                                    className={`late ${routeTimingReady && selectedDriverRouteMetrics.lateStopCount > 0 ? 'has-delay' : ''}`}
                                                    title={selectedDriverRouteMetrics.lateStopCount > 0
                                                        ? `${selectedDriverRouteMetrics.lateStopCount} scheduled pickup stop${selectedDriverRouteMetrics.lateStopCount === 1 ? '' : 's'} projected late`
                                                        : 'Projected lateness at scheduled pickup stops'}
                                                >
                                                    <ClockHistory size={11}/>
                                                    <b>
                                                        {routeTimingLoading
                                                            ? '…'
                                                            : routeTimingReady && selectedDriverRouteMetrics.scheduledStopCount > 0
                                                                ? selectedDriverRouteMetrics.lateStopCount > 0
                                                                    ? formatRouteDuration(selectedDriverRouteMetrics.maxLateMinutes)
                                                                    : 'None'
                                                                : '—'}
                                                    </b>
                                                    <small>{selectedDriverRouteMetrics.lateStopCount > 0 ? `${selectedDriverRouteMetrics.lateStopCount} late` : 'lateness'}</small>
                                                </span>
                                            </div>
                                            <ol>
                                                {selectedDriverRoutePlan.map((stop, stopIndex) => {
                                                    const isSelectedStop = String(stop.pickupId) === String(primarySelectedPickupId);
                                                    const hasMissingFirstLeg = stopIndex === 0 && !selectedDriverLocation;
                                                    const driveTimingLabel = routeTimingLoading
                                                        ? 'Drive calculating'
                                                        : !routeTimingReady
                                                            ? 'Drive unavailable'
                                                            : hasMissingFirstLeg
                                                                ? 'First-leg drive unavailable'
                                                                : `Drive ${formatRouteDuration(stop.driveMinutes)}`;
                                                    const scheduleTimingLabel = stop.lateMinutes > 0
                                                        ? `Late ${formatRouteDuration(stop.lateMinutes)}`
                                                        : stop.waitMinutes > 0
                                                            ? `Wait ${formatRouteDuration(stop.waitMinutes)}`
                                                            : stop.isScheduledTarget ? 'On time' : null;
                                                    const stopTimingTitle = [
                                                        formatRoutePlanWindow(stop),
                                                        driveTimingLabel,
                                                        `Service ${formatRouteDuration(stop.serviceMinutes)}`,
                                                        routeTimingReady ? scheduleTimingLabel : null,
                                                    ].filter(Boolean).join(' · ');

                                                    return (
                                                        <li
                                                            key={stop.key}
                                                            className={`${isSelectedStop ? 'selected' : ''} ${draggedRouteStopKey === stop.key ? 'dragging' : ''}`}
                                                            style={{'--dispatch-route-color': getDeliveryRouteColor(stop.pickupId)}}
                                                            draggable
                                                            onDragStart={(event) => {
                                                                event.dataTransfer.effectAllowed = 'move';
                                                                event.dataTransfer.setData('text/plain', stop.key);
                                                                setDraggedRouteStopKey(stop.key);
                                                            }}
                                                            onDragEnd={() => setDraggedRouteStopKey(null)}
                                                            onDragOver={(event) => {
                                                                event.preventDefault();
                                                                event.dataTransfer.dropEffect = 'move';
                                                            }}
                                                            onDrop={(event) => {
                                                                event.preventDefault();
                                                                const sourceKey = draggedRouteStopKey || event.dataTransfer.getData('text/plain');
                                                                const sourceIndex = selectedDriverRoutePlan.findIndex((item) => item.key === sourceKey);
                                                                movePlannedStop(sourceKey, stop.key, sourceIndex < stopIndex ? 'after' : 'before');
                                                                setDraggedRouteStopKey(null);
                                                            }}
                                                        >
                                                            <span className="dispatch-route-drag-handle" title="Drag to resequence" aria-hidden="true">
                                                                <GripVertical size={13}/>
                                                            </span>
                                                            <button
                                                                type="button"
                                                                className="dispatch-route-stop-main"
                                                                onClick={() => {
                                                                    setPickupSelectionSource('driver');
                                                                    setSelectedPickupId(stop.pickupId);
                                                                    setAssignmentMessage(null);
                                                                    openSidebar('deliveries');
                                                                }}
                                                                aria-label={`Select delivery ${stop.pickupId}, route stop ${stop.sequence}. ${stopTimingTitle}`}
                                                                title={stopTimingTitle}
                                                            >
                                                                <i className={`dispatch-route-stop-number ${stop.kind}`}>{stop.sequence}</i>
                                                                <span>
                                                                    <small>
                                                                        {stop.kind === 'origin' ? 'Pickup' : 'Drop-off'} · {!stop.assigned ? 'Proposed ' : ''}Delivery #{stop.pickupId}
                                                                    </small>
                                                                    <strong>{stop.displayAddress || stop.address}</strong>
                                                                    <span className="dispatch-route-stop-timing" aria-hidden="true">
                                                                        <em className="drive">{driveTimingLabel}</em>
                                                                        <em className="service">Service {formatRouteDuration(stop.serviceMinutes)}</em>
                                                                        {routeTimingReady && scheduleTimingLabel && (
                                                                            <em className={stop.lateMinutes > 0 ? 'late' : stop.waitMinutes > 0 ? 'wait' : 'on-time'}>
                                                                                {scheduleTimingLabel}
                                                                            </em>
                                                                        )}
                                                                    </span>
                                                                </span>
                                                                <time dateTime={new Date(stop.arrivesAt).toISOString()}>
                                                                    <small>ETA {formatRoutePlanTime(stop.arrivesAt)}</small>
                                                                    <strong>{formatRoutePlanTime(stop.startsAt)}–{formatRoutePlanTime(stop.endsAt)}</strong>
                                                                </time>
                                                            </button>
                                                            <span className="dispatch-route-move-actions">
                                                                <button
                                                                    type="button"
                                                                    disabled={stopIndex === 0}
                                                                    onClick={() => movePlannedStop(stop.key, selectedDriverRoutePlan[stopIndex - 1]?.key, 'before')}
                                                                    aria-label={`Move route stop ${stop.sequence} earlier`}
                                                                ><ChevronUp/></button>
                                                                <button
                                                                    type="button"
                                                                    disabled={stopIndex === selectedDriverRoutePlan.length - 1}
                                                                    onClick={() => movePlannedStop(stop.key, selectedDriverRoutePlan[stopIndex + 1]?.key, 'after')}
                                                                    aria-label={`Move route stop ${stop.sequence} later`}
                                                                ><ChevronDown/></button>
                                                            </span>
                                                        </li>
                                                    );
                                                })}
                                            </ol>
                                            {routePlanNotice && <p className="notice">{routePlanNotice}</p>}
                                            <p className="route-source">
                                                {routeTimingReady
                                                    ? !selectedDriverLocation
                                                        ? 'Road ETAs begin at the first stop; the drive to that stop is omitted because the driver has no GPS location.'
                                                        : !selectedDriverLocationIsLive
                                                            ? `Road ETAs start from the driver's last GPS location (${formatRelativeTime(selectedDriverLocation.updatedAt, now)}) and may shift when a fresh location arrives.`
                                                            : 'Road distance and planned driving ETAs are included.'
                                                    : routeTimingLoading
                                                        ? 'Calculating road driving time and ETAs…'
                                                        : 'Road driving time is unavailable; total and finish show service and schedule minimums only.'}
                                                {' '}Route changes are a dispatcher preview until route persistence is added.
                                            </p>
                                        </>
                                    ) : (
                                        <p className="empty">No remaining stops have map coordinates.</p>
                                    ))}
                                </section>
                                </section>
                            , dispatchPageElement)}
                        </>
                    )}
                </div>
            </aside>

            {driversOpen && !mobileDispatchLayout && (
                <div
                    className="dispatch-sidebar-resize-handle drivers"
                    style={{left: driversPanelWidth}}
                    role="separator"
                    tabIndex={0}
                    aria-label="Resize drivers panel"
                    aria-controls="dispatch-drivers-panel"
                    aria-orientation="vertical"
                    aria-valuemin={SIDEBAR_MIN_WIDTH}
                    aria-valuemax={driversPanelMaxWidth}
                    aria-valuenow={driversPanelWidth}
                    aria-valuetext={`${driversPanelWidth} pixels wide`}
                    title="Drag or use the arrow keys to resize Drivers"
                    onMouseDown={(event) => beginResize('drivers', event, driversPanelWidth, driversPanelMaxWidth)}
                    onTouchStart={(event) => beginResize('drivers', event, driversPanelWidth, driversPanelMaxWidth)}
                    onKeyDown={(event) => resizeWithKeyboard('drivers', event, driversPanelWidth, driversPanelMaxWidth)}
                >
                    <GripVertical aria-hidden="true"/>
                </div>
            )}

            {!driversOpen && (
                <button type="button" className="dispatch-sidebar-reveal left" onClick={() => openSidebar('drivers')} aria-label="Show drivers panel">
                    <Truck size={18}/><span>Drivers</span><ChevronRight size={16}/>
                </button>
            )}

            <aside
                id="dispatch-deliveries-panel"
                ref={deliveriesSidebarRef}
                className={`dispatch-sidebar dispatch-deliveries-sidebar ${deliveriesOpen ? 'open' : 'closed'}`}
                aria-label="Deliveries"
                style={mobileDispatchLayout ? undefined : {width: deliveriesPanelWidth}}
            >
                <div className="dispatch-sidebar-header">
                    <div>
                        <span className="dispatch-sidebar-eyebrow">Operations</span>
                        <h2>Deliveries <span>{displayedPickups.length}</span></h2>
                    </div>
                    <button type="button" className="dispatch-sidebar-close" onClick={() => setDeliveriesOpen(false)} aria-label="Hide deliveries panel">
                        <ChevronRight size={19}/>
                    </button>
                </div>

                <div className="dispatch-sidebar-controls">
                    <label className="dispatch-search">
                        <Search size={15}/>
                        <input
                            type="search"
                            value={pickupSearch}
                            onChange={(event) => setPickupSearch(event.target.value)}
                            placeholder="ID, address, customer..."
                            aria-label="Search deliveries"
                        />
                        {pickupSearch && (
                            <button type="button" onClick={() => setPickupSearch('')} aria-label="Clear delivery search"><X size={15}/></button>
                        )}
                    </label>

                    <div className="dispatch-segmented dispatch-delivery-filters" aria-label="Filter deliveries">
                        {[
                            ['all', `All ${deliveryFilterScope.length}`],
                            ['unassigned', `Unassigned ${deliveryFilterUnassignedCount}`],
                            ['active', `Active ${deliveryFilterScope.length - deliveryFilterUnassignedCount}`],
                        ].map(([value, label]) => (
                            <button
                                key={value}
                                type="button"
                                className={pickupStatusFilter === value ? 'active' : ''}
                                onClick={() => setPickupStatusFilter(value)}
                                aria-pressed={pickupStatusFilter === value}
                            >
                                {label}
                            </button>
                        ))}
                        <button
                            type="button"
                            className={scheduledOnly ? 'active' : ''}
                            onClick={() => setScheduledOnly((current) => !current)}
                            aria-pressed={scheduledOnly}
                            title="Show only deliveries with a scheduled pickup time"
                        >
                            Scheduled {scheduledPickupCount}
                        </button>
                    </div>
                </div>

                <div className="dispatch-sidebar-list dispatch-delivery-list">
                    {displayedPickups.map((pickup) => {
                        const isSelected = selectedPickupIdSet.has(String(pickup.id));
                        const locations = getPickupLocations(pickup);
                        const pickupTruckType = isNaviWhitelabelRecord(pickup.truck_type)
                            ? pickup.truck_type
                            : null;
                        const assignedDriverLocation = pickup.driver?.id ? driverLocations[pickup.driver.id] : null;
                        const assignedDriverPresence = driverPresence[pickup.driver?.id];
                        const assignedDriverIsOnline = isDriverOnline(assignedDriverPresence, now);
                        const originAddress = locations.origins[0]?.displayAddress
                            || locations.origins[0]?.address
                            || pickup.origin_address
                            || 'Pickup location';
                        const dropoffAddress = locations.dropoffs[0]?.displayAddress
                            || locations.dropoffs[0]?.address
                            || pickup.dropoff_address
                            || 'Drop-off location';

                        return (
                            <button
                                type="button"
                                key={pickup.id}
                                className={`dispatch-delivery-card ${isSelected ? 'selected' : ''}`}
                                style={{'--dispatch-route-color': getDeliveryRouteColor(pickup.id)}}
                                onClick={() => selectPickup(pickup.id)}
                                aria-pressed={isSelected}
                                aria-label={`${isSelected ? 'Deselect' : 'Select'} delivery ${pickup.id}`}
                            >
                                <span className="dispatch-delivery-card-header">
                                    <span className="dispatch-delivery-card-title">
                                        <i className="dispatch-delivery-selection-toggle" aria-hidden="true">
                                            {isSelected && <CheckCircleFill/>}
                                        </i>
                                        <strong>Delivery #{pickup.id}</strong>
                                    </span>
                                    <em className={getPickupStatusClass(pickup)}>{getPickupStatusLabel(pickup)}</em>
                                </span>
                                <span className="dispatch-route-summary">
                                    <span><i className="origin"/><b>{originAddress}</b></span>
                                    <span><i className="dropoff"/><b>{dropoffAddress}</b></span>
                                </span>
                                <span className="dispatch-delivery-meta">
                                    <small className="vehicle-type">
                                        <TruckTypeIcon truckType={pickupTruckType}/>
                                        <span>{pickupTruckType?.title || 'Vehicle TBD'}</span>
                                    </small>
                                    <small><ClockHistory size={13}/>{formatScheduledTime(pickup)}</small>
                                </span>
                                {pickup.driver && (
                                    <span className="dispatch-assigned-driver">
                                        <span>{getDriverInitials(pickup.driver.name)}</span>
                                        {pickup.driver.name || `Driver #${pickup.driver.id}`}
                                        <i className={assignedDriverIsOnline ? '' : 'offline'} title={getDriverLocationLabel(assignedDriverLocation, now)}>
                                            <Broadcast size={11}/>
                                            {getDriverConnectionLabel(assignedDriverPresence, now)}
                                        </i>
                                    </span>
                                )}
                                {!pickup.driver && hasPickupDriver(pickup) && (
                                    <span className="dispatch-assigned-driver">
                                        <span>?</span>
                                        Assigned outside Navi dispatch
                                        <i className="offline">Driver hidden</i>
                                    </span>
                                )}
                            </button>
                        );
                    })}

                    {!displayedPickups.length && (
                        <SidebarEmptyState
                            icon={<BoxSeam size={22}/>}
                            title="No deliveries found"
                            copy={dispatchPickups.length ? 'Try another search or status filter.' : 'There are no active deliveries right now.'}
                        />
                    )}
                </div>

                <div className={`dispatch-assignment-panel ${selectedPickup ? 'has-selection' : ''}`}>
                    {!selectedPickup ? (
                        <div className="dispatch-assignment-guide">
                            <span><GeoAltFill size={17}/></span>
                            <p><strong>Select a delivery</strong><small>View its route, driver, and assignment options.</small></p>
                        </div>
                    ) : (
                        <>
                            <div className="dispatch-assignment-heading">
                                <span>
                                    <small>
                                        {selectedPickups.length > 1
                                            ? `${selectedPickups.length} selected deliveries`
                                            : 'Selected delivery'}
                                    </small>
                                    <strong>
                                        #{selectedPickup.id}
                                        {selectedPickups.length > 1 ? ` +${selectedPickups.length - 1} more` : ''}
                                    </strong>
                                </span>
                                <span className="dispatch-assignment-actions">
                                    <Link to={`/pickups/${selectedPickup.id}`}>
                                        {selectedPickups.length > 1 ? `View #${selectedPickup.id}` : 'View details'}
                                    </Link>
                                    <button type="button" onClick={clearSelectedPickups}>Clear</button>
                                </span>
                            </div>

                            {selectedPickup.driver ? (
                                <div className="dispatch-tracking-card">
                                    <span className="dispatch-driver-avatar small">
                                        {getDriverInitials(selectedPickup.driver.name)}
                                        <i className={selectedPickupDriverIsOnline ? 'live' : 'offline'}/>
                                    </span>
                                    <span>
                                        <small>Assigned driver</small>
                                        <strong>{selectedPickup.driver.name || `Driver #${selectedPickup.driver.id}`}</strong>
                                        <small>{getDriverLocationLabel(selectedPickupDriverLocation, now)}</small>
                                    </span>
                                    <em className={selectedPickupDriverIsOnline ? '' : 'offline'}>
                                        <Broadcast size={12}/>
                                        {getDriverConnectionLabel(selectedPickupDriverPresence, now)}
                                    </em>
                                </div>
                            ) : hasPickupDriver(selectedPickup) ? (
                                <div className="dispatch-tracking-card">
                                    <span className="dispatch-driver-avatar small">?</span>
                                    <span>
                                        <small>Assigned driver</small>
                                        <strong>Outside Navi dispatch</strong>
                                    </span>
                                    <em className="offline">Driver details hidden</em>
                                </div>
                            ) : (
                                <>
                                    <div className={`dispatch-assignment-driver ${selectedDriver ? '' : 'empty'}`}>
                                        {selectedDriver ? (
                                            <>
                                                <span className="dispatch-driver-avatar small">{getDriverInitials(selectedDriver.name)}<i className={selectedDriverIsOnline ? 'live' : 'offline'}/></span>
                                                <span>
                                                    <small>Assign to</small>
                                                    <strong>{selectedDriver.name}</strong>
                                                </span>
                                                <button
                                                    type="button"
                                                    onClick={clearSelectedDriver}
                                                >
                                                    Change
                                                </button>
                                            </>
                                        ) : (
                                            <>
                                                <Truck size={17}/>
                                                <span><strong>Choose a driver</strong><small>Select one from the fleet panel or map.</small></span>
                                                {!driversOpen && <button type="button" onClick={() => openSidebar('drivers')}>Open</button>}
                                            </>
                                        )}
                                    </div>

                                    {selectedDriver && !compatible && (
                                        <p className="dispatch-compatibility-warning">
                                            This driver does not have the {isNaviWhitelabelRecord(selectedPickup.truck_type)
                                                ? selectedPickup.truck_type.title
                                                : 'required'} vehicle.
                                        </p>
                                    )}

                                    {selectedDriver && compatible && selectedDriverAvailability !== 'available' && (
                                        <p className="dispatch-compatibility-warning">
                                            {selectedDriverAvailability === 'busy'
                                                ? selectedDriverActivePickups.length > 1
                                                    ? `${selectedDriver.name} is already handling ${selectedDriverActivePickups.length} deliveries.`
                                                    : `${selectedDriver.name} is already handling delivery #${selectedDriverPickup?.id}.`
                                                : `${selectedDriver.name} is not currently marked available.`}
                                        </p>
                                    )}

                                    <button
                                        type="button"
                                        className="dispatch-assign-button"
                                        disabled={assignmentDisabled}
                                        onClick={assignDriver}
                                    >
                                        {assigning ? <><span className="dispatch-button-spinner"/>Assigning...</> : <><Truck size={17}/>Assign driver</>}
                                    </button>

                                    {!canAssign && <small className="dispatch-permission-note">You need delivery update permission to assign drivers.</small>}
                                </>
                            )}
                        </>
                    )}
                </div>
            </aside>

            {deliveriesOpen && !mobileDispatchLayout && (
                <div
                    className="dispatch-sidebar-resize-handle deliveries"
                    style={{right: deliveriesPanelWidth}}
                    role="separator"
                    tabIndex={0}
                    aria-label="Resize deliveries panel"
                    aria-controls="dispatch-deliveries-panel"
                    aria-orientation="vertical"
                    aria-valuemin={SIDEBAR_MIN_WIDTH}
                    aria-valuemax={deliveriesPanelMaxWidth}
                    aria-valuenow={deliveriesPanelWidth}
                    aria-valuetext={`${deliveriesPanelWidth} pixels wide`}
                    title="Drag or use the arrow keys to resize Deliveries"
                    onMouseDown={(event) => beginResize('deliveries', event, deliveriesPanelWidth, deliveriesPanelMaxWidth)}
                    onTouchStart={(event) => beginResize('deliveries', event, deliveriesPanelWidth, deliveriesPanelMaxWidth)}
                    onKeyDown={(event) => resizeWithKeyboard('deliveries', event, deliveriesPanelWidth, deliveriesPanelMaxWidth)}
                >
                    <GripVertical aria-hidden="true"/>
                </div>
            )}

            {!deliveriesOpen && (
                <button type="button" className="dispatch-sidebar-reveal right" onClick={() => openSidebar('deliveries')} aria-label="Show deliveries panel">
                    <ChevronLeft size={16}/><span>Deliveries</span><BoxSeam size={17}/>
                </button>
            )}

        </div>
    );
};

export default withAuth(DispatcherDashboard);
